import React, { useState } from "react";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Stack,
    Box,
    Typography,
} from "@mui/material";
import GiftUsernamesModal from "../../modal/GiftUsernamesModal";
import GiftUserGiftsModal from "../../modal/GiftUserGiftsModal";
import PushGiftNotificationModal from "../../modal/PushGiftNotificationModal";

interface UserGift {
    user: {
        name: string;
    };
    sas_username: string;
    is_used: boolean;
}

interface GiftType {
    id: string;
    name: string;
}

interface GiftDetailsProps {
    gift: {
        gift_type?: GiftType;
        compensate_type?: string;
        valid_from?: string;
        valid_to?: string;
        is_disabled?: boolean;
        is_published?: boolean;
        amount?: number;
        affected_users?: number;
        created_at?: string;
        updated_at?: string;
        usernames?: string[];
        user_gifts?: UserGift[];
        [key: string]: any;
    };
}

const ShowGiftDetails = ({ gift }: GiftDetailsProps) => {
    const [open, setOpen] = useState(false);
    const [usernamesModalOpen, setUsernamesModalOpen] = useState(false);
    const [userGiftsModalOpen, setUserGiftsModalOpen] = useState(false);
    const [notificationModalOpen, setNotificationModalOpen] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Check if gift is still valid (valid_to date is in the future)
    const isGiftValid = gift.valid_to
        ? new Date(gift.valid_to) > new Date()
        : false;

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleClickOpen}
                sx={{ minWidth: "fit-content" }}
            >
                مشاهدة
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                sx={{
                    "& .MuiPaper-root": {
                        minWidth: { xs: "290px", sm: "600px" },
                        maxHeight: "80vh",
                    },
                }}
            >
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        تفاصيل الهدية
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {/* Gift Details in Key-Value Format */}
                        {gift && (
                            <Box>
                                <Stack spacing={1}>
                                    {/* Gift Type */}
                                    {gift.gift_type && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                py: 1,
                                                borderBottom:
                                                    "1px solid #f0f0f0",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                نوع الهدية:
                                            </Typography>
                                            <Typography variant="body2">
                                                {gift.gift_type.name}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Compensate Type */}
                                    {gift.compensate_type && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                py: 1,
                                                borderBottom:
                                                    "1px solid #f0f0f0",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                النوع :
                                            </Typography>
                                            <Typography variant="body2">
                                                {gift.compensate_type ===
                                                    "days"
                                                    ? "أيام"
                                                    : gift.compensate_type ===
                                                        "traffic"
                                                        ? "بيانات"
                                                        : gift.compensate_type ===
                                                            "both"
                                                            ? "كلاهما"
                                                            : gift.compensate_type}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Amount */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            py: 1,
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: "bold" }}
                                        >
                                            قيمة البيانات:
                                        </Typography>
                                        <Typography variant="body2">
                                            {gift.traffic_amount || 0}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            py: 1,
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: "bold" }}
                                        >
                                            عدد الأيام:
                                        </Typography>
                                        <Typography variant="body2">
                                            {gift.days_amount || 0}
                                        </Typography>
                                    </Box>

                                    {/* Affected Users */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            py: 1,
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: "bold" }}
                                        >
                                            المستخدمين المتأثرين:
                                        </Typography>
                                        <Typography variant="body2">
                                            {gift?.gift_stats?.affected_users || 0}
                                        </Typography>
                                    </Box>
                                    {/* "affected_users": 1, */}
                                    {/* "affected_system_users": 3, */}
                                    {/* claimed Users */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            py: 1,
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: "bold" }}
                                        >
                                            المستخدمين المتحصلين علي الهدية:
                                        </Typography>
                                        <Typography variant="body2">
                                            {gift?.gift_stats?.redeemed_users || 0}
                                        </Typography>
                                    </Box>

                                    {/* Valid From */}
                                    {gift.valid_from && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                py: 1,
                                                borderBottom:
                                                    "1px solid #f0f0f0",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                صالح من:
                                            </Typography>
                                            <Typography variant="body2">
                                                {new Date(
                                                    gift.valid_from
                                                ).toLocaleString("ar-US")}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Valid To */}
                                    {gift.valid_to && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                py: 1,
                                                borderBottom:
                                                    "1px solid #f0f0f0",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                صالح حتى:
                                            </Typography>
                                            <Typography variant="body2">
                                                {new Date(
                                                    gift.valid_to
                                                ).toLocaleString("ar-US")}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Note */}
                                    <Box
                                        sx={{
                                            py: 1,
                                            borderBottom:
                                                "1px solid #f0f0f0",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: "bold", mb: 1 }}
                                        >
                                            الملاحظة:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {gift.note || "لا يوجد"}
                                        </Typography>
                                    </Box>

                                    {/* Comment */}
                                    <Box
                                        sx={{
                                            py: 1,
                                            borderBottom:
                                                "1px solid #f0f0f0",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: "bold", mb: 1 }}
                                        >
                                            نص الاشعار:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {gift.comment || "لا يوجد"}
                                        </Typography>
                                    </Box>

                                    {/* Usernames */}
                                    {gift.id && (
                                        <Box
                                            sx={{
                                                py: 1,
                                                borderBottom:
                                                    "1px solid #f0f0f0",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    أسماء الحسابات:
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() =>
                                                        setUsernamesModalOpen(
                                                            true
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: "fit-content",
                                                    }}
                                                >
                                                    عرض الكل
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* User Gifts */}
                                    {gift.id && (
                                        <Box sx={{ py: 1 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    المستخدمين:
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() =>
                                                        setUserGiftsModalOpen(
                                                            true
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: "fit-content",
                                                    }}
                                                >
                                                    عرض الكل
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Push Notification Button */}
                                    {gift.id && (
                                        <Box sx={{ py: 2, borderTop: "1px solid #f0f0f0", mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="medium"
                                                onClick={() =>
                                                    setNotificationModalOpen(true)
                                                }
                                                fullWidth
                                                disabled={!isGiftValid}
                                            >
                                                ارسال اشعار للمستخدمين
                                            </Button>
                                            {!isGiftValid && gift.valid_to && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ mt: 1, display: "block", textAlign: "center" }}
                                                >
                                                    هذه الهدية انتهت صلاحيتها
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}

                        {/* Empty state */}
                        {!gift && (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    لا توجد تفاصيل متاحة لهذه الهدية
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Gift Usernames Modal */}
            {gift.id && (
                <GiftUsernamesModal
                    open={usernamesModalOpen}
                    onClose={() => setUsernamesModalOpen(false)}
                    giftId={gift.id}
                />
            )}

            {/* Gift User Gifts Modal */}
            {gift.id && (
                <GiftUserGiftsModal
                    open={userGiftsModalOpen}
                    onClose={() => setUserGiftsModalOpen(false)}
                    giftId={gift.id}
                />
            )}

            {/* Push Gift Notification Modal */}
            {gift.id && (
                <PushGiftNotificationModal
                    open={notificationModalOpen}
                    onClose={() => setNotificationModalOpen(false)}
                    giftId={gift.id}
                />
            )}
        </>
    );
};

export default ShowGiftDetails;
